import { useState } from 'react';
import { AlertCircle, Loader2, Building2, Target, MessageSquare, Zap, TrendingUp, Clock, ChevronRight, Plus, RotateCcw, Trash2, Users, ShieldX, Megaphone, Phone, Mail, Mic } from 'lucide-react';
import VoiceProfileForm from '../components/voice-profile/VoiceProfileForm';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import useScrapeJob from '../hooks/useScrapeJob';

function ProfileView({ profile, onRegenerate, onNewProfile, history, onLoadProfile, onDeleteProfile, currentJobId }) {
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{profile.companyName || 'Company Profile'}</h2>
            <p className="text-xs text-slate-500">{profile.website || ''}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {history.length > 1 && (
            <Button variant="secondary" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <Clock className="w-3.5 h-3.5 mr-1" />
              History ({history.length})
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={onNewProfile}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            New
          </Button>
          <Button variant="secondary" size="sm" onClick={onRegenerate}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Regenerate
          </Button>
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <Card>
          <CardContent className="py-3">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Past Profiles</p>
            <div className="space-y-1">
              {history.map((item) => (
                <div
                  key={item.jobId}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors
                    ${item.jobId === currentJobId
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-slate-50 text-slate-700'
                    }`}
                >
                  <button
                    onClick={() => { onLoadProfile(item.jobId); setShowHistory(false); }}
                    className="flex-1 text-left"
                  >
                    <p className="font-medium">{item.companyName}</p>
                    <p className="text-xs text-slate-400">{item.url} &middot; {new Date(item.createdAt).toLocaleDateString()}</p>
                  </button>
                  <div className="flex items-center gap-1 ml-2">
                    {item.jobId !== currentJobId && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteProfile(item.jobId); }}
                        className="p-1 text-slate-300 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Description */}
      {profile.description && (
        <Card>
          <CardContent>
            <p className="text-sm text-slate-600 leading-relaxed">{profile.description}</p>
            <div className="flex gap-3 mt-3">
              {profile.industry && (
                <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">{profile.industry}</span>
              )}
              {profile.estimatedSize && (
                <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">Size: {profile.estimatedSize}</span>
              )}
              {profile.targetMarket && (
                <span className="px-2 py-1 bg-blue-50 rounded text-xs text-blue-600">{profile.targetMarket}</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Services */}
        {profile.services?.length > 0 && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-primary-500" />
                <h3 className="text-sm font-semibold text-slate-900">Services</h3>
              </div>
              <ul className="space-y-1.5">
                {profile.services.map((s, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-1.5 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Key Messages */}
        {profile.keyMessages?.length > 0 && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-green-500" />
                <h3 className="text-sm font-semibold text-slate-900">Key Messages</h3>
              </div>
              <ul className="space-y-1.5">
                {profile.keyMessages.map((m, i) => (
                  <li key={i} className="text-sm text-slate-600 italic">"{m}"</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Talking Points */}
        {profile.talkingPoints?.length > 0 && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-slate-900">Talking Points</h3>
              </div>
              <ul className="space-y-1.5">
                {profile.talkingPoints.map((t, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Pain Points */}
        {profile.painPoints?.length > 0 && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-semibold text-slate-900">Pain Points</h3>
              </div>
              <ul className="space-y-1.5">
                {profile.painPoints.map((p, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Voice Profile Section ── */}
      {profile.voice_profile && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 pt-2 border-t border-slate-200">
            Brand Voice Profile
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Communication Style + Content Personality */}
            <Card>
              <CardContent>
                <div className="flex items-center gap-2 mb-3">
                  <Mic className="w-4 h-4 text-violet-500" />
                  <h3 className="text-sm font-semibold text-slate-900">Communication Style</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">{profile.voice_profile.communication_style}</p>
                {profile.voice_profile.content_personality && (
                  <div>
                    <span className="text-xs font-medium text-slate-400 uppercase">Content Personality</span>
                    <p className="text-sm text-slate-600 mt-0.5">{profile.voice_profile.content_personality}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vocabulary Preferences */}
            {profile.voice_profile.vocabulary_preferences?.length > 0 && (
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-indigo-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Vocabulary & Phrases</h3>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.voice_profile.vocabulary_preferences.map((v, i) => (
                      <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs">"{v}"</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Opinion Map */}
            {profile.voice_profile.opinion_map && Object.keys(profile.voice_profile.opinion_map).length > 0 && (
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-teal-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Opinion Map</h3>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(profile.voice_profile.opinion_map).map(([topic, stance]) => (
                      <div key={topic}>
                        <span className="text-xs font-medium text-slate-400 uppercase">{topic.replace(/_/g, ' ')}</span>
                        <p className="text-sm text-slate-600">{stance}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dos & Don'ts */}
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {profile.voice_profile.dos?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
                        <h3 className="text-sm font-semibold text-slate-900">Do</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {profile.voice_profile.dos.map((d, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />{d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {profile.voice_profile.donts?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="w-5 h-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-bold">✗</span>
                        <h3 className="text-sm font-semibold text-slate-900">Don't</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {profile.voice_profile.donts.map((d, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />{d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ── ICP Section ── */}
      {profile.icp && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 pt-2 border-t border-slate-200">
            Ideal Customer Profile & Messaging
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Primary ICP */}
            {profile.icp.primary_icp && (
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-blue-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Primary ICP</h3>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    {profile.icp.primary_icp.industries?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Industries</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {profile.icp.primary_icp.industries.map((ind, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{ind}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {profile.icp.primary_icp.company_size && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Company Size</span>
                        <p>{profile.icp.primary_icp.company_size}</p>
                      </div>
                    )}
                    {profile.icp.primary_icp.geography && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Geography</span>
                        <p>{profile.icp.primary_icp.geography}</p>
                      </div>
                    )}
                    {profile.icp.primary_icp.target_titles?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Target Titles</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {profile.icp.primary_icp.target_titles.map((t, i) => (
                            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {profile.icp.primary_icp.key_pain_points?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Key Pain Points</span>
                        <ul className="mt-1 space-y-1">
                          {profile.icp.primary_icp.key_pain_points.map((p, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />{p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Secondary ICP */}
            {profile.icp.secondary_icp && (
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-purple-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Secondary ICP</h3>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    {profile.icp.secondary_icp.industries?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Industries</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {profile.icp.secondary_icp.industries.map((ind, i) => (
                            <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">{ind}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {profile.icp.secondary_icp.company_size && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Company Size</span>
                        <p>{profile.icp.secondary_icp.company_size}</p>
                      </div>
                    )}
                    {profile.icp.secondary_icp.target_titles?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-400 uppercase">Target Titles</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {profile.icp.secondary_icp.target_titles.map((t, i) => (
                            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Anti-ICP */}
            {profile.icp.anti_icp && (
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldX className="w-4 h-4 text-red-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Anti-ICP (Avoid)</h3>
                  </div>
                  {profile.icp.anti_icp.description && (
                    <p className="text-sm text-slate-500 mb-2">{profile.icp.anti_icp.description}</p>
                  )}
                  {profile.icp.anti_icp.criteria?.length > 0 && (
                    <ul className="space-y-1.5">
                      {profile.icp.anti_icp.criteria.map((c, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />{c}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Messaging Framework */}
          {profile.icp.messaging_framework && (
            <div className="space-y-4">
              {profile.icp.messaging_framework.elevator_pitch && (
                <Card>
                  <CardContent>
                    <div className="flex items-center gap-2 mb-2">
                      <Megaphone className="w-4 h-4 text-amber-500" />
                      <h3 className="text-sm font-semibold text-slate-900">Elevator Pitch</h3>
                    </div>
                    <p className="text-sm text-slate-700 italic bg-amber-50 rounded-lg px-4 py-3">
                      "{profile.icp.messaging_framework.elevator_pitch}"
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Service Lines */}
              {profile.icp.messaging_framework.service_lines?.length > 0 && (
                <Card>
                  <CardContent>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Service Line Messaging</h3>
                    <div className="space-y-4">
                      {profile.icp.messaging_framework.service_lines.map((svc, i) => (
                        <div key={i} className="border border-slate-100 rounded-lg p-3">
                          <p className="font-medium text-sm text-slate-900 mb-1">{svc.name}</p>
                          {svc.value_prop && <p className="text-xs text-slate-600 mb-1"><span className="font-medium text-slate-500">Value:</span> {svc.value_prop}</p>}
                          {svc.differentiator && <p className="text-xs text-slate-600 mb-1"><span className="font-medium text-slate-500">Differentiator:</span> {svc.differentiator}</p>}
                          {svc.objection_responses && Object.keys(svc.objection_responses).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-slate-400 uppercase mb-1">Objection Responses</p>
                              {Object.entries(svc.objection_responses).map(([key, val]) => (
                                <p key={key} className="text-xs text-slate-600 ml-2">
                                  <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {val}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Email Hooks & Call Openers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.icp.messaging_framework.email_hooks?.length > 0 && (
                  <Card>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-3">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <h3 className="text-sm font-semibold text-slate-900">Email Hooks</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {profile.icp.messaging_framework.email_hooks.map((h, i) => (
                          <li key={i} className="text-sm text-slate-600 italic">"{h}"</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
                {profile.icp.messaging_framework.call_openers?.length > 0 && (
                  <Card>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-3">
                        <Phone className="w-4 h-4 text-green-500" />
                        <h3 className="text-sm font-semibold text-slate-900">Call Openers</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {profile.icp.messaging_framework.call_openers.map((c, i) => (
                          <li key={i} className="text-sm text-slate-600 italic">"{c}"</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VoiceProfile() {
  const [lastUrl, setLastUrl] = useState('');
  const { status, profile, error, history, currentJobId, startScrape, loadProfile, deleteProfile, reset } = useScrapeJob();

  // Derive page state from hook status
  let state;
  if (status === 'loading') state = 'loading';       // initial page load
  else if (status === 'pending') state = 'scraping';  // generating or loading from history
  else if (status === 'completed' && profile) state = 'profile';
  else if (status === 'failed') state = 'error';
  else state = 'input';

  const handleGenerate = async ({ url, transcript, notes }) => {
    setLastUrl(url);
    await startScrape(url, { transcript, notes });
  };

  const handleRegenerate = () => {
    if (lastUrl) {
      handleGenerate({ url: lastUrl });
    } else {
      reset();
    }
  };

  const handleNewProfile = () => {
    reset();
  };

  const handleLoadProfile = (jobId) => {
    loadProfile(jobId);
  };

  const handleReset = () => {
    reset();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Voice Profile & ICP</h1>
        <p className="text-sm text-slate-500 mt-1">
          Define how your brand communicates and who your ideal customers are.
        </p>
      </div>

      {state === 'input' && (
        <>
          <VoiceProfileForm onGenerate={handleGenerate} loading={false} />
          {/* Show history below the form if there are past profiles */}
          {history.length > 0 && (
            <Card>
              <CardContent>
                <p className="text-sm font-semibold text-slate-900 mb-3">Previous Profiles</p>
                <div className="space-y-1">
                  {history.map((item) => (
                    <div
                      key={item.jobId}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <button
                        onClick={() => handleLoadProfile(item.jobId)}
                        className="flex-1 text-left"
                      >
                        <p className="text-sm font-medium text-slate-700">{item.companyName}</p>
                        <p className="text-xs text-slate-400">{item.url} &middot; {new Date(item.createdAt).toLocaleDateString()}</p>
                      </button>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={() => deleteProfile(item.jobId)}
                          className="p-1 text-slate-300 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {state === 'loading' && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-500">Loading your profile...</p>
          </CardContent>
        </Card>
      )}

      {state === 'scraping' && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Analyzing Website</h3>
            <p className="text-sm text-slate-500">
              Generating company profile for <span className="font-medium text-slate-700">{lastUrl}</span>
            </p>
            <p className="text-xs text-slate-400 mt-2">This may take a few seconds...</p>
          </CardContent>
        </Card>
      )}

      {state === 'profile' && profile && (
        <ProfileView
          profile={profile}
          onRegenerate={handleRegenerate}
          onNewProfile={handleNewProfile}
          history={history}
          onLoadProfile={handleLoadProfile}
          onDeleteProfile={deleteProfile}
          currentJobId={currentJobId}
        />
      )}

      {state === 'error' && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Analysis Failed</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
              {error || 'Something went wrong. Please try again.'}
            </p>
            <Button variant="secondary" onClick={handleReset}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
